// Minimal ANSI-SGR renderer for the live tofu logs. OpenTofu is now run WITHOUT
// -no-color, so its output carries colour escape codes (red errors, green adds,
// yellow changes, dim notes). This parses the common SGR sequences into styled
// spans so the log panel shows colour instead of raw `\x1b[31m` gibberish — the
// point being that a failure jumps out in red at a glance.
import { Fragment, type CSSProperties } from 'react';
import { color } from './tokens';

// Standard 16-colour terminal palette mapped onto the app's colour language.
const FG: Record<number, string> = {
  30: '#5f6772', // black → dim
  31: color.red,
  32: color.green,
  33: color.amber,
  34: color.blue,
  35: '#c678dd', // magenta
  36: '#56b6c2', // cyan
  37: '#c2c8d1', // white
  90: '#7b828d', // bright black (gray)
  91: '#e8949b',
  92: color.greenBright,
  93: '#e6b95c',
  94: '#7ab6ea',
  95: '#d79be6',
  96: '#74c7d3',
  97: '#e7eaef',
};

type SgrState = { color?: string; bold?: boolean; dim?: boolean };

const applyCodes = (state: SgrState, codes: number[]): SgrState => {
  let next = { ...state };
  for (const code of codes) {
    if (code === 0) next = {};
    else if (code === 1) next.bold = true;
    else if (code === 2) next.dim = true;
    else if (code === 22) next.bold = next.dim = undefined;
    else if (code === 39) next.color = undefined;
    else if (FG[code]) next.color = FG[code];
  }
  return next;
};

const styleFor = (state: SgrState): CSSProperties => ({
  color: state.color,
  fontWeight: state.bold ? 600 : undefined,
  opacity: state.dim ? 0.65 : undefined,
});

// eslint-disable-next-line no-control-regex
const SGR = /\x1b\[([0-9;]*)m/g;

export const AnsiText = ({
  text,
  fallback,
  style,
}: {
  text?: string;
  fallback?: string;
  style?: CSSProperties;
}) => {
  const source = text ?? '';
  if (!source) {
    return <>{fallback ?? ''}</>;
  }

  const parts: { text: string; state: SgrState }[] = [];
  let state: SgrState = {};
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  SGR.lastIndex = 0;
  while ((match = SGR.exec(source))) {
    if (match.index > lastIndex) {
      parts.push({ text: source.slice(lastIndex, match.index), state });
    }
    const codes = (match[1] || '0').split(';').map(n => Number(n) || 0);
    state = applyCodes(state, codes);
    lastIndex = SGR.lastIndex;
  }
  if (lastIndex < source.length) {
    parts.push({ text: source.slice(lastIndex), state });
  }

  return (
    <span style={style}>
      {parts.map((part, i) => {
        const css = styleFor(part.state);
        const hasStyle = css.color || css.fontWeight || css.opacity;
        return hasStyle ? (
          <span key={i} style={css}>
            {part.text}
          </span>
        ) : (
          <Fragment key={i}>{part.text}</Fragment>
        );
      })}
    </span>
  );
};
